import { Body1, Body1Strong, Caption1, Subtitle2, Tag, makeStyles, tokens } from '@fluentui/react-components';

import { TChangeTemplate, TTemplateInputValue } from '../../models';
import { resolveRequestPrefills } from '../../utils';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  title: { color: tokens.colorNeutralForeground1 },
  metaTags: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  metaTag: { fontWeight: tokens.fontWeightRegular },
  fields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  field: { display: 'flex', flexDirection: 'column', gap: '2px' },
  // Description carries the most text; a slightly smaller body keeps it readable.
  description: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 },
  muted: { color: tokens.colorNeutralForeground3 },
});

const LabelledValue = ({ label, value, small }: { label: string; value: string; small?: boolean }) => {
  const classes = useStyles();
  if (!value) return null;
  return (
    <div className={classes.field}>
      <Body1Strong>{label}</Body1Strong>
      {small ? <Body1 className={classes.description}>{value}</Body1> : <Body1>{value}</Body1>}
    </div>
  );
};

type TProps = {
  template: TChangeTemplate;
  inputValues: Record<string, TTemplateInputValue>;
};

// The live "Generated request" preview. It always shows Title and Description and
// updates immediately as inputs change. Empty required tokens render as neutral
// `[Field label]` placeholders — raw `{{input:...}}` syntax is never shown.
export const GeneratedRequestPreview = ({ template, inputValues }: TProps) => {
  const classes = useStyles();
  const resolved = resolveRequestPrefills(template, inputValues, 'preview');
  const metaTags = [resolved.scopeType, resolved.changeCategory].filter(Boolean) as string[];

  return (
    <div className={classes.root}>
      <div className={classes.field}>
        <Caption1 className={classes.muted}>Title</Caption1>
        <Subtitle2 className={classes.title}>{resolved.title || 'Untitled change'}</Subtitle2>
      </div>

      {metaTags.length > 0 ? (
        <div className={classes.metaTags}>
          {metaTags.map((tag) => (
            <Tag key={tag} className={classes.metaTag} appearance="outline" shape="rounded" size="small" value={tag}>
              {tag}
            </Tag>
          ))}
        </div>
      ) : null}

      <div className={classes.fields}>
        <LabelledValue label="Description" value={resolved.description} small />
        <LabelledValue label="Reason or trigger" value={resolved.reasonOrTrigger} />
        <LabelledValue label="Desired outcome" value={resolved.desiredOutcome} />
        <LabelledValue label="Initial implementation proposal" value={resolved.implementationProposal} />
      </div>
    </div>
  );
};
